import React, { useEffect, useState } from "react"
import { TextField, FormControl } from "@mui/material"
import Autocomplete from "@mui/material/Autocomplete"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import { FormContextType, useForm } from "../../../contexts/formContext"
import { FormInput } from "../../../components/ui/Input"
import { FormSelect } from "../../../components/ui/Select"
import useCheckoutShippingAddress from "../../hooks/useCheckoutShippingAddress"
import theme from "../../../material-ui/theme"
import { getLs } from "../../../utils/localStorage"

export interface PlaceType {
  structured_formatting: {
    secondary_text: string
    main_text: string
    main_text_matched_substrings: [
      {
        offset: number
        length: number
      }
    ]
  }
}

export type AddressDetailsResp = {
  address_components: Array<{
    long_name: string
    short_name: string
    types: string[]
  }>
}

export const VAT_REQUIRED_COUNTRIES = [
  "Switzerland",
  "Sweden",
  "Norway",
  "Germany",
  "United Kingdom",
]

interface AddressProps {
  type: "shipping" | "billing"
  includeCardHolderName?: boolean
  onChange?: () => {}
}

const CheckoutShippingAddress: React.FC<AddressProps> = ({
  type,
  includeCardHolderName,
  onChange,
}) => {
  const { formState, formActions } = useForm() as FormContextType

  const [
    autocompleteOptions,
    handleChange,
    handleSelectAddress,
    handleKeyDown,
    handleOnFocus,
    handleOnBlur,
    countryList,
  ] = useCheckoutShippingAddress(type, onChange)
  const [open, setOpen] = useState(false)
  const closePopper = () => setOpen(false)
  const openPopper = () => setOpen(true)

  useEffect(() => {
    const countryVal = formState.inputs[`${type}Country`]?.value
    if (!countryVal) {
      const invCountry = getLs("country_for_inventory")
      if (invCountry) {
        formState.inputs[`${type}Country`].value = invCountry
      }
    }
  }, [])

  return (
    <>
      {type === "shipping" && (
        <Grid item xs={12}>
          <FormControl variant="standard" fullWidth>
            <FormInput
              fieldId="address-first-name"
              fieldName={`${type}FirstName`}
              inputs={formState.inputs}
              inputHandler={formActions.inputHandler}
              autoComplete="off"
            />
          </FormControl>
        </Grid>
      )}
      {type === "shipping" && (
        <Grid item xs={12}>
          <FormControl variant="standard" fullWidth>
            <FormInput
              fieldId="address-last-name"
              fieldName={`${type}LastName`}
              inputs={formState.inputs}
              inputHandler={formActions.inputHandler}
              autoComplete="off"
            />
          </FormControl>
        </Grid>
      )}
      {type === "billing" && includeCardHolderName && (
        <Grid item xs={12}>
          <FormControl variant="standard" fullWidth>
            <FormInput
              fieldId="payment-cardholderName"
              fieldName="cardHolderName"
              inputs={formState.inputs}
              inputHandler={formActions.inputHandler}
            />
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormSelect
            fieldId="country-select"
            fieldName={`${type}Country`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            values={countryList}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          id="sensai-address"
          style={{ width: "100%", backgroundColor: "white" }}
          getOptionLabel={(option: any) =>
            typeof option === "string" ? option : option.description
          }
          open={open}
          onOpen={openPopper}
          onClose={closePopper}
          filterOptions={(x: PlaceType[]) => x}
          options={autocompleteOptions}
          autoComplete
          includeInputInList
          filterSelectedOptions
          inputValue={formState.inputs[`${type}Street`].value}
          renderInput={(params: any) => {
            const formField = formState.inputs[`${type}Street`]

            return (
              <TextField
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                autoComplete="off"
                {...params}
                label="Street Address"
                required={true}
                variant="outlined"
                fullWidth
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                error={!!formField.error}
                helperText={formField.error}
              />
            )
          }}
          renderOption={(x: any, option: PlaceType) => {
            return (
              <Grid
                container
                alignItems="center"
                onClick={async () => {
                  await handleSelectAddress(option)()
                  closePopper()
                }}
              >
                <Grid item>
                  <LocationOnIcon
                    sx={{
                      color: theme.palette.text.secondary,
                      marginRight: theme.spacing(2),
                    }}
                  />
                </Grid>
                <Grid item xs>
                  {option.structured_formatting.main_text}
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ marginBottom: "5px" }}
                  >
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            )
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-unit"
            fieldName={`${type}Unit`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-city"
            fieldName={`${type}City`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-province"
            fieldName={`${type}Province`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-postal"
            fieldName={`${type}Postal`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <FormInput
            fieldId="address-phone"
            fieldName={`${type}Phone`}
            inputs={formState.inputs}
            inputHandler={formActions.inputHandler}
            autoComplete="off"
          />
        </FormControl>
      </Grid>
      {type === "shipping" &&
        VAT_REQUIRED_COUNTRIES.includes(
          formState.inputs.shippingCountry?.value
        ) && (
          <Grid item xs={12}>
            <FormControl variant="standard" fullWidth>
              <FormInput
                fieldId="address-vat"
                fieldName={`${type}Vat`}
                inputs={formState.inputs}
                inputHandler={formActions.inputHandler}
                autoComplete="off"
              />
            </FormControl>
          </Grid>
        )}
    </>
  )
}

export default CheckoutShippingAddress
